// FINANCE FORK: global excluded-flow-categories setting. Every flow-derived
// dashboard number (savings rate, YTD totals, FI progress, trends, movers,
// Sankey) drops these expense categories — the fix for mis-classified
// transfer-like categories (e.g. retirement contributions imported as a
// "Transfers" expense) distorting all of them at once.
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';

import { ExcludeCategoriesButton } from '#components/reports/ExcludeCategoriesButton';
import { useCategories } from '#hooks/useCategories';
import { useExcludedFlowCategories } from '#hooks/useExcludedFlowCategories';

import { Setting } from './UI';

export function DashboardFlowSettings() {
  const { t } = useTranslation();
  const { data: { grouped: groupedCategories = [] } = { grouped: [] } } =
    useCategories();
  const [excludedCategories, setExcludedCategories] =
    useExcludedFlowCategories();

  // Exclusions only ever apply to the expense side of flow queries, so only
  // offer expense groups for selection.
  const expenseGroups = useMemo(
    () => groupedCategories.filter(group => !group.is_income),
    [groupedCategories],
  );
  const expenseCategories = useMemo(
    () => expenseGroups.flatMap(group => group.categories ?? []),
    [expenseGroups],
  );

  return (
    <Setting
      primaryAction={
        <ExcludeCategoriesButton
          groupedCategories={expenseGroups}
          categories={expenseCategories}
          excludedCategories={excludedCategories}
          onChange={setExcludedCategories}
          title={t('Exclude from dashboard numbers')}
          placement="bottom start"
        />
      }
    >
      <Text>
        <Trans>
          <strong>Dashboard numbers</strong> — expense categories excluded here
          are dropped from every flow-derived dashboard card (savings rate, YTD
          totals, FI progress, trends, movers, Sankey). Transactions are not
          modified. The list syncs across devices.
        </Trans>
      </Text>
    </Setting>
  );
}
